import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import tarefaAdminService, { TarefaAdminFiltros } from '../../services/tarefaAdminService';
import usuarioService from '../../services/usuarioService';
import departamentoService from '../../services/departamentoService';
import { Tarefa, Usuario, Departamento } from '../../types';
import TaskDetailsModal from '../kanban/TaskDetailsModal';
import { obterCorPrioridade as obterCorPrioridadeCompartilhada } from '../../utils/coresPrioridade';

const BuscaTarefasAdmin: React.FC = () => {
  const [carregando, setCarregando] = useState(false);
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [tarefaSelecionada, setTarefaSelecionada] = useState<Tarefa | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  
  // Filtros
  const [filtros, setFiltros] = useState<TarefaAdminFiltros>({
    texto: '',
    status: '',
    prioridade: '',
    responsavel: '',
    criador: '',
    departamento: '',
    dataInicio: '',
    dataFim: '',
    arquivadas: false,
    pagina: 1,
    limite: 20
  });
  const [exibir, setExibir] = useState<'ativas' | 'arquivadas' | 'todas'>('ativas');

  // Carregar usuários e departamentos
  useEffect(() => {
    carregarUsuarios();
    carregarDepartamentos();
  }, []);

  const carregarUsuarios = async () => {
    try {
      const response = await usuarioService.obterTodos();
      if (response.success && response.usuarios) {
        setUsuarios(response.usuarios);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const carregarDepartamentos = async () => {
    try {
      const response = await departamentoService.obterTodos();
      if (response.success && response.departamentos) {
        setDepartamentos(response.departamentos);
      }
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
    }
  };

  const buscarTarefas = async () => {
    try {
      setCarregando(true);
      const filtrosParaBusca = {
        ...filtros,
        arquivadas: exibir === 'arquivadas' ? true : exibir === 'todas' ? undefined : false,
        pagina,
        limite: 20,
        carregarConcluidas: true,
        diasConcluidas: 365 // Buscar tarefas concluídas do último ano
      };

      console.log('🔍 Buscando tarefas com filtros:', filtrosParaBusca);
      const response = await tarefaAdminService.listarTarefasGlobais(filtrosParaBusca);
      
      if (response && response.tarefas) {
        // Mapear os dados da API para o formato esperado pelo componente
        const tarefasMapeadas = response.tarefas.map((t: any) => ({
          ...t,
          responsavel: t.responsavel_id ? {
            id: t.responsavel_id,
            nome: t.responsavel_nome || '',
            email: t.responsavel_email || ''
          } : undefined,
          criador: t.criador_id ? {
            id: t.criador_id,
            nome: t.criador_nome || '',
            email: t.criador_email || ''
          } : undefined,
          departamento: t.departamento_id ? {
            id: t.departamento_id,
            nome: t.departamento_nome || ''
          } : undefined,
          dataCriacao: t.data_criacao,
          dataVencimento: t.data_vencimento,
          dataAtualizacao: t.data_atualizacao,
          dataConclusao: t.data_conclusao
        }));
        
        setTarefas(tarefasMapeadas);
        setTotal(response.total);
        setTotalPaginas(response.totalPaginas);
      }
    } catch (error) {
      console.error('Erro ao buscar tarefas:', error);
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      texto: '',
      status: '',
      prioridade: '',
      responsavel: '',
      criador: '',
      departamento: '',
      dataInicio: '',
      dataFim: '',
      arquivadas: false,
      pagina: 1,
      limite: 20
    });
    setExibir('ativas');
    setTarefas([]);
    setTotal(0);
    setPagina(1);
  };

  const handleFiltroChange = (campo: keyof TarefaAdminFiltros, valor: any) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handlePaginaChange = (event: React.ChangeEvent<unknown>, novaPagina: number) => {
    setPagina(novaPagina);
    setFiltros(prev => ({ ...prev, pagina: novaPagina }));
    // Buscar automaticamente ao mudar página
    setTimeout(() => buscarTarefas(), 100);
  };

  const obterCorStatus = (status: string): string => {
    const cores: Record<string, string> = {
      'Pendente': '#FFA726',
      'Solicitado': '#FFA726',
      'A iniciar': '#42A5F5',
      'Em Andamento': '#66BB6A',
      'Em Execução': '#66BB6A',
      'Aguardando Resposta': '#FFA726',
      'Aguardando Aprovação': '#AB47BC',
      'Ajustes Requisitados': '#EF5350',
      'Concluída': '#26A69A',
      'Finalizada': '#26A69A'
    };
    return cores[status] || '#757575';
  };

  // Mesma paleta de prioridade usada em todo o resto do sistema (antes essa
  // tela tinha sua própria escala de cores Material hardcoded, diferente da
  // paleta de marca usada no Kanban/modal de tarefa).
  const obterCorPrioridade = obterCorPrioridadeCompartilhada;

  const formatarData = (data: string | Date | undefined): string => {
    if (!data) return '-';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  };

  const visualizarTarefa = (tarefa: Tarefa) => {
    setTarefaSelecionada(tarefa);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setTarefaSelecionada(null);
    // Recarregar a lista após fechar o modal (caso tenha sido editada)
    buscarTarefas();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ mr: 1 }} />
          Busca Avançada de Tarefas
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Busque tarefas usando múltiplos filtros
        </Typography>
      </Box>

      {/* Formulário de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            {/* Linha 1: Busca e Status */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Buscar por título ou descrição"
                value={filtros.texto || ''}
                onChange={(e) => handleFiltroChange('texto', e.target.value)}
                placeholder="Digite palavras-chave..."
                variant="outlined"
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filtros.status || ''}
                  onChange={(e) => handleFiltroChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  <MenuItem value="A iniciar">A iniciar</MenuItem>
                  <MenuItem value="Em Andamento">Em Andamento</MenuItem>
                  <MenuItem value="Aguardando Aprovação">Aguardando Aprovação</MenuItem>
                  <MenuItem value="Ajustes Requisitados">Ajustes Requisitados</MenuItem>
                  <MenuItem value="Concluída">Concluída</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Linha 2: Prioridade e Departamento */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={filtros.prioridade || ''}
                  onChange={(e) => handleFiltroChange('prioridade', e.target.value)}
                  label="Prioridade"
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="baixa">Baixa</MenuItem>
                  <MenuItem value="media">Média</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
              <Autocomplete
                options={departamentos}
                getOptionLabel={(option) => option.nome}
                value={departamentos.find(d => d.id === filtros.departamento) || null}
                onChange={(event, newValue) => handleFiltroChange('departamento', newValue?.id || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Departamento" variant="outlined" />
                )}
              />
            </Box>

            {/* Linha 3: Responsável e Criador */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Autocomplete
                options={usuarios}
                getOptionLabel={(option) => {
                  const dept = option.departamento_nome || 'Sem departamento';
                  return `${option.nome} (${dept})`;
                }}
                value={usuarios.find(u => u.id === filtros.responsavel) || null}
                onChange={(event, newValue) => handleFiltroChange('responsavel', newValue?.id || '')}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.nome.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.departamento_nome || '').toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Responsável" variant="outlined" placeholder="Digite para buscar..." />
                )}
              />
              <Autocomplete
                options={usuarios}
                getOptionLabel={(option) => {
                  const dept = option.departamento_nome || 'Sem departamento';
                  return `${option.nome} (${dept})`;
                }}
                value={usuarios.find(u => u.id === filtros.criador) || null}
                onChange={(event, newValue) => handleFiltroChange('criador', newValue?.id || '')}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.nome.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.departamento_nome || '').toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Criador" variant="outlined" placeholder="Digite para buscar..." />
                )}
              />
            </Box>

            {/* Linha 4: Datas e Arquivadas */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Data de Criação (Início)"
                value={filtros.dataInicio || ''}
                onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
              <TextField
                fullWidth
                type="date"
                label="Data de Criação (Fim)"
                value={filtros.dataFim || ''}
                onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel>Exibir</InputLabel>
                <Select
                  value={exibir}
                  onChange={(e) => setExibir(e.target.value as 'ativas' | 'arquivadas' | 'todas')}
                  label="Exibir"
                >
                  <MenuItem value="ativas">Tarefas Ativas</MenuItem>
                  <MenuItem value="arquivadas">Tarefas Arquivadas</MenuItem>
                  <MenuItem value="todas">Todas as Tarefas</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Botões */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limparFiltros}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={buscarTarefas}
                disabled={carregando}
              >
                {carregando ? 'Buscando...' : 'Buscar'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Resultados */}
      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tarefas.length > 0 ? (
        <>
          {/* Informações */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Encontradas {total} tarefa(s) | Página {pagina} de {totalPaginas}
            </Typography>
          </Box>

          {/* Tabela de Resultados */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell>Departamento</TableCell>
                  <TableCell>Data Criação</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tarefas.map((tarefa: any) => (
                  <TableRow key={tarefa.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {tarefa.id.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tarefa.titulo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tarefa.status}
                        size="small"
                        sx={{
                          backgroundColor: obterCorStatus(tarefa.status),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tarefa.prioridade}
                        size="small"
                        sx={{
                          backgroundColor: obterCorPrioridade(tarefa.prioridade),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tarefa.responsavel_nome || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tarefa.departamento_nome || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatarData(tarefa.data_criacao)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => visualizarTarefa(tarefa)}
                      >
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          {totalPaginas > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPaginas}
                page={pagina}
                onChange={handlePaginaChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <Alert severity="info">
          Use os filtros acima e clique em "Buscar" para encontrar tarefas.
        </Alert>
      )}

      {/* Modal de Detalhes da Tarefa */}
      {tarefaSelecionada && (
        <TaskDetailsModal
          tarefa={tarefaSelecionada}
          aberto={modalAberto}
          onFechar={fecharModal}
        />
      )}
    </Box>
  );
};

export default BuscaTarefasAdmin;
